import React, { useEffect, useState, useContext } from 'react';
import './Sidebar.css';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { RefreshContext } from '../../../App';
import {
    IoChevronBack, IoChevronForward, IoGridOutline, IoBriefcaseOutline,
    IoPeopleOutline, IoSettingsOutline, IoLogOutOutline, IoConstructOutline,
    IoChevronDown, IoLayersOutline, IoDocumentTextOutline, IoBuildOutline
} from 'react-icons/io5';
import { BsPersonGear } from 'react-icons/bs';
import { HiOutlineUsers, HiOutlineUserGroup } from 'react-icons/hi';
import { MdOutlineChecklist, MdWorkOutline } from "react-icons/md";
import { getNodeServiceBaseUrl, getSwaggerServiceBaseUrl } from "../../../config/apiBase";
import { getActiveApp, getAppHomePath, isApp } from "../../../config/activeModule";
import BrandLogo from "../../../shared-ui/BrandLogo";
import platformLogo from "../../../assets/branding/cranesoftwares-logo.png";
import "../../../shared-ui/crane-tokens.css";

// --- Environment Variables ---
const ZTASK_TOOLS_BASE_URL = getNodeServiceBaseUrl();
const ZTOOLS_BASE_URL = getSwaggerServiceBaseUrl();
const ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR = process.env.REACT_APP_ZTOOL_URL;
const GET_COMPANY_DETAILS_ENDPOINT = `${ZTOOLS_BASE_URL}/api/get-company`;
const GET_USERS_ENDPOINT = `${ZTOOLS_BASE_URL}/api/get-users`;

const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';

// --- Skeleton and Loader Components ---
const SidebarSkeleton = ({ isCollapsed }) => (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header"><div className={`skeleton-block ${isCollapsed ? 'logo-skeleton-collapsed' : 'logo-skeleton-expanded'}`}></div></div>
        <div className="sidebar-content"><div className="menu-group">{!isCollapsed && <div className="skeleton-line menu-title-skeleton"></div>}<ul>{Array.from({ length: 5 }).map((_, index) => (<li key={index} className="skeleton-menu-item"><div className="skeleton-block icon-skeleton-sidebar"></div>{!isCollapsed && <div className="skeleton-line text-skeleton-sidebar"></div>}</li>))}</ul></div></div>
        <div className="sidebar-footer"><div className="skeleton-user-profile"><div className="skeleton-block avatar-skeleton"></div>{!isCollapsed && (<div className="skeleton-user-info"><div className="skeleton-line user-name-skeleton"></div><div className="skeleton-line user-subtext-skeleton"></div></div>)}</div></div>
    </div>
);

const ProfileInfoLoader = () => (
    <div className="user-additional-info">
        <div className="info-item"><span className="info-label">Role:</span><span className="info-value info-loader">Loading...</span></div>
        <div className="info-item"><span className="info-label">Designation:</span><span className="info-value info-loader">Loading...</span></div>
        <div className="info-item"><span className="info-label">Department:</span><span className="info-value info-loader">Loading...</span></div>
    </div>
);


const Sidebar = ({ refreshKey }) => {
    const getInitialCollapsedState = () => localStorage.getItem('sidebarCollapsed') === 'true';
    const location = useLocation();
    
    const { triggerRefresh } = useContext(RefreshContext);

    const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
    const [userDetails, setUserDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [companyName, setCompanyName] = useState('Loading...');
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState('#');
    const [companyKeyword, setCompanyKeyword] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [systemStatus, setSystemStatus] = useState({ status: 'checking', label: 'Checking systems…' });
    const [, setActiveAppTick] = useState(0);

    const toggleProfileDropdown = () => setIsProfileOpen(prevState => !prevState);

    useEffect(() => {
        const handleProfileUpdate = () => {
            const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            if (storedUserDetailsString) {
                try {
                    setUserDetails(JSON.parse(storedUserDetailsString));
                } catch(e) { console.error("Sidebar: Failed to parse user details.", e); }
            }
        };
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        const onAppChange = () => setActiveAppTick((n) => n + 1);
        window.addEventListener('z-active-app', onAppChange);
        return () => window.removeEventListener('z-active-app', onAppChange);
    }, []);

    // Real platform DB status — never hardcode "all systems operational"
    useEffect(() => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            setSystemStatus({ status: 'unknown', label: 'Status unavailable' });
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(`${ZTASK_TOOLS_BASE_URL}/api/platform/system-status`, {
                    headers: { Authorization: `Bearer ${token}`, 'X-Client': 'web' },
                });
                const body = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (res.ok && body?.data?.status === 'operational') {
                    setSystemStatus({
                        status: 'ok',
                        label: `DB operational${body.data.latency_ms != null ? ` (${body.data.latency_ms}ms)` : ''}`,
                    });
                } else {
                    setSystemStatus({
                        status: 'degraded',
                        label: body?.data?.message || body?.message || 'Systems degraded',
                    });
                }
            } catch {
                if (!cancelled) setSystemStatus({ status: 'degraded', label: 'Platform unreachable' });
            }
        };
        load();
        const id = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    useEffect(() => {
        const loadSidebarData = async () => {
            if (!userDetails) setIsLoading(true); else setIsUpdatingProfile(true);
            const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
            let storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            if (userDetails) { storedUserDetailsString = JSON.stringify(userDetails); }
            if (!storedToken || !storedUserDetailsString) { if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR); return; }
            let parsedUserDetails;
            try { parsedUserDetails = JSON.parse(storedUserDetailsString); if (!userDetails) setUserDetails(parsedUserDetails); } catch (e) { console.error("Sidebar: Failed to parse userDetails.", e); localStorage.clear(); if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR); return; }
            try {
                const [companyDetailsResponse, usersResponse] = await Promise.all([ fetch(GET_COMPANY_DETAILS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } }), fetch(GET_USERS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } }) ]);
                if (companyDetailsResponse.ok) { 
                    const companyData = await companyDetailsResponse.json(); 
                    const companyList = companyData.message || companyData.messgae; 
                    if (Array.isArray(companyList) && companyList.length > 0) { 
                        const companyInfo = companyList[0]; 
                        setCompanyName(companyInfo.companyName || 'Company'); 
                        setCompanyLogoUrl(companyInfo.logoUrl); 
                        setCompanyKeyword(companyInfo.companyKeyword || companyInfo.company_abbr || companyInfo.companyShortName || 'ZO');

                        const rawUrl = companyInfo.websiteUrl;
                        if (rawUrl && typeof rawUrl === 'string') {
                            if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
                                setCompanyWebsiteUrl(rawUrl);
                            } else {
                                setCompanyWebsiteUrl(`https://${rawUrl}`);
                            }
                        } else {
                            setCompanyWebsiteUrl('#');
                        }
                    } else { 
                        setCompanyName('My App'); 
                    } 
                } else { 
                    setCompanyName('My App'); 
                }
                let role = 'N/A', designation = 'N/A', department = 'N/A';
                if (usersResponse.ok) { const usersData = await usersResponse.json(); if (Array.isArray(usersData.userData)) { const currentUser = usersData.userData.find(user => user.email === parsedUserDetails.email); if (currentUser) { role = currentUser.role?.name || 'N/A'; designation = currentUser.designation?.designationName || 'N/A'; department = currentUser.department?.departmentName || 'N/A'; } } }
                setCurrentUserData({ role: role.toUpperCase(), designation: designation.toUpperCase(), department: department.toUpperCase() });
            } catch (error) { console.error("Error fetching sidebar data:", error); setCurrentUserData({ role: (userDetails?.role?.name || 'N/A').toUpperCase(), designation: (userDetails?.designation?.designationName || 'N/A').toUpperCase(), department: (userDetails?.department?.departmentName || 'N/A').toUpperCase() }); } finally { setIsLoading(false); setIsUpdatingProfile(false); }
        };
        loadSidebarData();
    }, [refreshKey, userDetails]);

    const toggleSidebarCollapse = () => {
        setIsCollapsed(prevState => {
            const newState = !prevState;
            localStorage.setItem('sidebarCollapsed', newState);
            return newState;
        });
    };

    const handleNavLinkClick = (e, path) => {
        if (location.pathname === path) {
            e.preventDefault();
            triggerRefresh();
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        try { sessionStorage.removeItem('zActiveApp'); } catch (_) { /* ignore */ }
        if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) {
            const base = ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR.replace(/\/$/, '');
            window.location.replace(`${base}/login`);
        }
    };
    
    // Platform BrandLogo is always the shell brand (Stitch).
    // Tenant/company logo is shown only as secondary context when configured — never replaces platform identity on collapse.
    const LogoContent = () => (
      <div className="logo" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {isCollapsed ? (
          <BrandLogo src={platformLogo} collapsed size="sm" decorative={false} alt="CraneSoftwares" />
        ) : (
          <>
            <BrandLogo src={platformLogo} size="md" showWordmark alt="CraneSoftwares" />
            {companyLogoUrl && (
              <img
                src={companyLogoUrl}
                alt=""
                title={`${companyName} (tenant)`}
                className="logo-image"
                style={{ height: 22, marginLeft: 4, opacity: 0.9 }}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </div>
    );
    
    if (isLoading) return <SidebarSkeleton isCollapsed={isCollapsed} />;
    if (!userDetails) return null;
    
    const storedRole = (() => {
        try {
            const d = JSON.parse(localStorage.getItem(USER_DETAILS_KEY) || '{}');
            return (d.role?.name || d.role || '').toString().toUpperCase().replace(/\s+/g, '_');
        } catch {
            return '';
        }
    })();
    // Prefer JWT/local role; API profile can return "N/A" and wrongly hide admin menus
    const apiRole = (currentUserData?.role || '').toString().toUpperCase().replace(/\s+/g, '_');
    const userRole =
      storedRole ||
      (apiRole && apiRole !== 'N/A' ? apiRole : '') ||
      '';
    const isPlatformAdmin = userRole === 'SUPER_ADMIN' || userRole === 'ADMIN';
    const avatarInitial = userDetails.name ? userDetails.name.charAt(0).toUpperCase() : 'U';
    
    const menuItems = [];

    // Expected product menus (module-scoped; still one SPA)
    if (isApp("ZTASK")) {
        menuItems.push({ id: 'dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/dashboard' });
        menuItems.push({ id: 'tasks', icon: <MdWorkOutline />, text: 'Tasks', path: '/tasks' });
        menuItems.push({ id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/project' });
        menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' });
        menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' });
    }

    if (isApp("ZORDER")) {
        menuItems.push({ id: 'order-dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/order-dashboard' });
        menuItems.push({ id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/project' });
        menuItems.push({ id: 'quotations', icon: <IoDocumentTextOutline />, text: 'Quotations', path: '/quotations' });
        menuItems.push({ id: 'work-orders', icon: <MdOutlineChecklist />, text: 'Work Orders', path: '/work-orders' });
        menuItems.push({ id: 'invoices', icon: <IoDocumentTextOutline />, text: 'Invoices', path: '/invoices' });
        menuItems.push({ id: 'ar-debit-notes', icon: <IoDocumentTextOutline />, text: 'Debit Notes', path: '/ar/debit-notes' });
        menuItems.push({ id: 'ar-credit-notes', icon: <IoDocumentTextOutline />, text: 'Credit Notes', path: '/ar/credit-notes' });
        menuItems.push({ id: 'ar-receipts', icon: <IoDocumentTextOutline />, text: 'Receipts', path: '/ar/receipts' });
        menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' });
        menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' });
    }

    if (isApp("ZASSET")) {
        menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' });
        menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' });
        menuItems.push({ id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/project' });
    }

    if (isApp("ZCUSTOMER")) {
        menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' });
        menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' });
        menuItems.push({ id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/project' });
    }

    if (isApp("ZCOMPLIANCE")) {
        menuItems.push({ id: 'compliance', icon: <IoGridOutline />, text: 'Dashboard', path: '/compliance' });
        menuItems.push({ id: 'compliance-vehicle', icon: <IoConstructOutline />, text: 'Vehicle (VAHAN)', path: '/compliance/vehicle' });
        menuItems.push({ id: 'compliance-licence', icon: <HiOutlineUserGroup />, text: 'Licence (SARATHI)', path: '/compliance/licence' });
        menuItems.push({ id: 'compliance-history', icon: <MdOutlineChecklist />, text: 'History', path: '/compliance/history' });
        menuItems.push({ id: 'compliance-vehicles', icon: <IoConstructOutline />, text: 'Saved vehicles', path: '/compliance/vehicles' });
        menuItems.push({ id: 'compliance-licences', icon: <HiOutlineUserGroup />, text: 'Saved licences', path: '/compliance/licences' });
    }
    
    const organizationItems = [];
    if ((isPlatformAdmin || isApp("ZTASK") || isApp("ZORDER")) && (isApp("ZTASK") || isApp("ZORDER"))) {
        if (isPlatformAdmin) {
            organizationItems.push({ id: 'team', icon: <IoPeopleOutline />, text: 'Team', path: '/organization/team' });
            organizationItems.push({ id: 'adminManager', icon: <BsPersonGear />, text: 'User Management', path: '/organization/user-management'});
            organizationItems.push({ id: 'commonMaster', icon: <IoLayersOutline />, text: 'Common Master', path: '/organization/common-master'});
            if (isApp("ZORDER")) {
                organizationItems.push({ id: 'workType', icon: <MdWorkOutline />, text: 'Work Type', path: '/organization/work-types'});
            }
        }
        organizationItems.push({ id: 'teamMgmt', icon: <HiOutlineUsers />, text: 'Team Management', path: '/organization/team-management' });
    }

    const reportsItems = [];
    if (isApp("ZTASK") || isApp("ZORDER") || isApp("ZASSET") || isApp("ZCUSTOMER")) {
        reportsItems.push({ id: 'reports', icon: <IoDocumentTextOutline />, text: 'Reports', path: '/reports' });
    }
    // Always show Report Setting for ZTask/ZOrder (page still enforces auth)
    if (isApp("ZTASK") || isApp("ZORDER")) {
        reportsItems.push({ id: 'reportSettings', icon: <IoBuildOutline />, text: 'Report Setting', path: '/reports/settings' });
    }
    // Fallback: sticky app lost but user is still on a commerce/task surface
    if (!reportsItems.some((i) => i.id === 'reportSettings')) {
        const p = (location.pathname || '').toLowerCase();
        const onSharedCommerce =
            p.startsWith('/work-orders') ||
            p.startsWith('/order-dashboard') ||
            p.startsWith('/invoices') ||
            p.startsWith('/ar') ||
            p.startsWith('/tasks') ||
            p.startsWith('/project') ||
            p.startsWith('/reports');
        if (onSharedCommerce) {
            if (!reportsItems.some((i) => i.id === 'reports')) {
                reportsItems.push({ id: 'reports', icon: <IoDocumentTextOutline />, text: 'Reports', path: '/reports' });
            }
            reportsItems.push({ id: 'reportSettings', icon: <IoBuildOutline />, text: 'Report Setting', path: '/reports/settings' });
        }
    }

    // Stitch Administration section — available across modules (not ZORDER-only)
    // ZCompliance: omit Ops Dashboard (/ops-dashboard is a ZOrder surface and flipped sticky app).
    const administrationItems = [
        ...(isApp("ZCOMPLIANCE")
          ? []
          : [{ id: 'ops-dashboard', icon: <IoGridOutline />, text: 'Ops Dashboard', path: '/ops-dashboard' }]),
        { id: 'search', icon: <IoDocumentTextOutline />, text: 'Global Search', path: '/search' },
        { id: 'audit', icon: <IoLayersOutline />, text: 'Audit / Activity', path: '/audit' },
        { id: 'applications', icon: <IoLayersOutline />, text: 'Applications', path: '/platform/applications' },
        { id: 'roles-perms', icon: <BsPersonGear />, text: 'Roles & Permissions', path: '/settings/permissions' },
        { id: 'users-admin', icon: <HiOutlineUsers />, text: 'Users', path: '/organization/user-management' },
        { id: 'settingsUser', icon: <IoSettingsOutline />, text: 'Settings', path: '/platform/settings' },
    ];

    const renderMenuGroup = (title, items) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="menu-group">
                {!isCollapsed && <p className="menu-group-title">{title}</p>}
                <ul>
                    {items.map((item) => (
                        <li key={item.id} title={isCollapsed ? item.text : undefined}>
                            <NavLink
                                to={item.path}
                                className={({ isActive }) => `menu-item ${isActive ? 'selected' : ''}`}
                                onClick={(e) => handleNavLinkClick(e, item.path)}
                            >
                                <span className="menu-item-icon">{item.icon}</span>
                                {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`} data-active-app={getActiveApp()}>
             <div className="sidebar-header">
                <Link to={getAppHomePath()} className="logo-link" title="CraneSoftwares home" aria-label="CraneSoftwares home">
                  <LogoContent />
                </Link>
                <div onClick={toggleSidebarCollapse} className="collapse-icon-wrapper" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSidebarCollapse(); }} aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
                    {isCollapsed ? <IoChevronForward className="collapse-icon" /> : <IoChevronBack className="collapse-icon" />}
                </div>
            </div>
            <div className="sidebar-content">
                {renderMenuGroup('OPERATIONS', menuItems)}
                {renderMenuGroup('ORGANIZATION', organizationItems)}
                {renderMenuGroup('REPORTS', reportsItems)}
                {renderMenuGroup('ADMINISTRATION', administrationItems)}
                {!isCollapsed && (
                  <div className="menu-group" style={{ marginTop: 'auto', padding: '8px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 11,
                        color: systemStatus.status === 'ok' ? '#006948' : systemStatus.status === 'checking' ? '#3f4850' : '#ba1a1a',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: systemStatus.status === 'ok' ? '#006948' : systemStatus.status === 'checking' ? '#707881' : '#ba1a1a',
                          display: 'inline-block',
                        }}
                      />
                      {String(systemStatus.label || 'Status').toUpperCase()}
                    </div>
                    {companyName && companyName !== 'Loading...' && (
                      <div style={{ fontSize: 11, color: '#3f4850', marginTop: 4 }} title="Tenant context">{companyName}</div>
                    )}
                  </div>
                )}
            </div>
            <div className="sidebar-footer">
                <div className="footer-separator"></div>
                <div className="user-profile-wrapper">{!isCollapsed ? (<div className="user-profile-section"><Link to="/settings/profile" className="avatar-link" title="View Profile"><div className="avatar">{avatarInitial}</div></Link><div className="user-info"><div className="user-info-header" onClick={toggleProfileDropdown} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleProfileDropdown(); }}><span className="user-name">{userDetails.name || 'User'}</span><IoChevronDown className={`profile-dropdown-icon ${isProfileOpen ? 'open' : ''}`} /></div><Link to="/settings/profile" className="user-subtext-link"><span className="user-subtext">Profile • Settings</span></Link><div className={`user-details-container ${isProfileOpen ? 'open' : ''}`}>{isUpdatingProfile ? (<ProfileInfoLoader />) : (currentUserData && (<div className="user-additional-info"><div className="info-item"><span className="info-label">Role:</span><span className="info-value">{currentUserData.role}</span></div><div className="info-item"><span className="info-label">Designation:</span><span className="info-value">{currentUserData.designation}</span></div><div className="info-item"><span className="info-label">Department:</span><span className="info-value">{currentUserData.department}</span></div></div>))}</div></div></div>) : (<Link to="/settings/profile" className="user-profile-link" title={`${userDetails.name} - Profile & Settings`}><div className="user-profile-section collapsed-user-profile"><div className="avatar">{avatarInitial}</div></div></Link>)}</div>
                <div onClick={handleLogout} className="logout-section menu-item" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }} title={isCollapsed ? "Logout" : "Logout"}><span className="menu-item-icon"><IoLogOutOutline /></span>{!isCollapsed && <span className="menu-item-text">Logout</span>}</div>
                {ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR && (
                  <a
                    href={`${ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR.replace(/\/$/, '')}/dashboard`}
                    className="menu-item"
                    title="Back to ZTools launcher"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <span className="menu-item-icon"><IoChevronBack /></span>
                    {!isCollapsed && <span className="menu-item-text">Back to ZTools</span>}
                  </a>
                )}
            </div>
        </div>
    );
};

export default Sidebar;